import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import { METADATA } from '@flexo/reducers';
import { getOrgRole, isEndUser } from './roles';

type EndUserCommunityGuardProps = {
  children: React.ReactNode;
};

/**
 * FLEXO-6090 / FLEXO-6089 #7: data-isolation guard for end-user community routes.
 *
 * An end user may only view a community that belongs to their own entity set
 * (`/community/:id` where `:id` is one of their authorised community IDs,
 * derived from `metadata.entities`). Any attempt to open a foreign community
 * via direct URL is redirected to their own member aggregate. Staff
 * (admin/supervisor) are unaffected — the guard is a no-op for non-end-user
 * roles.
 *
 * The guard owns the entities preflight for deep links: do not render the
 * guarded module until ownership can be decided, otherwise module resolvers can
 * fire backend calls for foreign IDs before the redirect happens.
 */
export function EndUserCommunityGuard({ children }: EndUserCommunityGuardProps) {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const metadata = useSelector((state: any) => state.metadata);
  const { id } = useParams();

  const role = getOrgRole(user);
  const entities = (metadata?.entities as any) || null;
  const communities: any[] =
    entities?.community || entities?.communities || [];
  const entitiesStatus = metadata?.EntitiesAsyncStatus;
  const ownMemberId =
    user?.metadata?.flexo_user_id || user?.tokenContent?.flexo_user_id;
  const entitiesReady = entitiesStatus === 'SUCCESS' || communities.length > 0;

  useEffect(() => {
    if (
      isEndUser(role) &&
      !entitiesReady &&
      entitiesStatus !== 'PENDING' &&
      entitiesStatus !== 'ERROR'
    ) {
      dispatch(METADATA.SetEntitiesAsync({ dispatch }) as any);
    }
  }, [dispatch, entitiesReady, entitiesStatus, role]);

  if (!isEndUser(role)) {
    return <>{children}</>;
  }

  if (!entitiesReady) {
    if (entitiesStatus === 'ERROR') {
      return (
        <Navigate
          to={ownMemberId ? `/member/${ownMemberId}` : '/access-denied'}
          replace
        />
      );
    }
    return null;
  }

  const ownedCommunityIds = new Set<string>(
    communities.map((c: any) => c?.communityId).filter(Boolean),
  );

  if (id && !ownedCommunityIds.has(id)) {
    return (
      <Navigate
        to={ownMemberId ? `/member/${ownMemberId}` : '/access-denied'}
        replace
      />
    );
  }

  return <>{children}</>;
}
